import SelectionState from "state/selections/SelectionState";
import { selectDefinitions } from "./selectors";
import { useDispatch, useSelector } from "react-redux";
import { useMemo } from "react";
import { useQuery } from "react-query";
import {
  selectBaseGroupingParams,
  selectGlPamValue,
} from "state/selections/selector";
import { setDefinition } from "./actions";
import { commitSelections } from "state/selections/actions";
import scope from "./scope";
import { useIsFetching } from "react-query";
import { exchange } from "@ls/js-fetch";
import { format } from "date-fns";
import { selectCommittedEntities } from "state/selections/selector";
import { selectModeValue } from "state/selections/selector";
import Mode from "state/selections/Mode";

export const useColumnDefinitions = () => {
  const definition = useSelector(
    selectDefinitions(SelectionState.COMMITTED)
  )?.[0];
  const glPam = useSelector(selectGlPamValue(SelectionState.COMMITTED));
  const dispatch = useDispatch();
  const isFetching = useIsFetching([scope]);
  return useMemo(() => {
    const headerPam = !!glPam ? "PAM " : "";
    const columnDefs = [];
    if (definition?.realizedGlMTD) {
      columnDefs.push({
        headerName: `${headerPam}Realized G/L MTD`,
        colId: "mtdGainLoss",
        field: "mtdGainLoss",
        loading: isFetching,
        X_onDelete: () => {
          dispatch(setDefinition(0, definition.setRealizedGlMTD(false), true));
          dispatch(commitSelections());
        },
      });
    }
    if (definition?.realizedGlQTD) {
      columnDefs.push({
        headerName: `${headerPam}Realized G/L QTD`,
        colId: "qtdGainLoss",
        field: "qtdGainLoss",
        loading: isFetching,
        X_onDelete: () => {
          dispatch(setDefinition(0, definition.setRealizedGlQTD(false), true));
        },
      });
    }
    if (definition?.realizedGlYTD) {
      columnDefs.push({
        headerName: `${headerPam}Realized G/L YTD`,
        colId: "ytdGainLoss",
        field: "ytdGainLoss",
        loading: isFetching,
        X_onDelete: () => {
          dispatch(setDefinition(0, definition.setRealizedGlYTD(false), true));
        },
      });
    }

    if (definition?.perRealizedGlMTD) {
      columnDefs.push({
        headerName: `${headerPam}% Realized G/L MTD`,
        colId: "mtdGainLossPercentage",
        field: "mtdGainLossPercentage",
        loading: isFetching,
        X_onDelete: () => {
          dispatch(
            setDefinition(0, definition.setPerRealizedGlMTD(false), true)
          );
          dispatch(commitSelections());
        },
      });
    }
    if (definition?.perRealizedGlQTD) {
      columnDefs.push({
        headerName: `${headerPam} % Realized G/L QTD`,
        colId: "qtdGainLossPercentage",
        field: "qtdGainLossPercentage",
        loading: isFetching,
        X_onDelete: () => {
          dispatch(
            setDefinition(0, definition.setPerRealizedGlQTD(false), true)
          );
        },
      });
    }
    if (definition?.perRealizedGlYTD) {
      columnDefs.push({
        headerName: `%${headerPam} % Realized G/L YTD`,
        colId: "ytdGainLossPercentage",
        field: "ytdGainLossPercentage",
        loading: isFetching,
        X_onDelete: () => {
          dispatch(
            setDefinition(0, definition.setPerRealizedGlYTD(false), true)
          );
        },
      });
    }

    if (definition?.roiYTD) {
      columnDefs.push({
        headerName: ` ROI YTD`,
        colId: "roiYTD",
        field: "roiYTD",
        loading: isFetching,
        X_onDelete: () => {
          dispatch(setDefinition(0, definition.setRoiYTD(false), true));
        },
      });
    }
    return columnDefs;
  }, [definition, isFetching, dispatch, glPam]);
};

export const useRowsMap = () => {
  const dataQuery = useDataQuery();
  const dataQueryRoi = useRoiYTDQuery();
  const { data } = dataQuery;
  const { data: dataRoiYTD } = dataQueryRoi;
  return useMemo(() => {
    const map = {};
    const combinedData = [...(data || []), ...(dataRoiYTD || [])];
    combinedData?.forEach((row) => {
      if (!map[row.entityId]) {
        map[row.entityId] = {};
      }
      map[row.entityId].mtdGainLoss =
        row.mtdGainLoss ?? map[row.entityId]?.mtdGainLoss;
      map[row.entityId].qtdGainLoss =
        row.qtdGainLoss ?? map[row.entityId]?.qtdGainLoss;
      map[row.entityId].ytdGainLoss =
        row.ytdGainLoss ?? map[row.entityId]?.ytdGainLoss;
      map[row.entityId].mtdGainLossPercentage =
        row.mtdGainLossPercentage ?? map[row.entityId].mtdGainLossPercentage;
      map[row.entityId].qtdGainLossPercentage =
        row.qtdGainLossPercentage ?? map[row.entityId].qtdGainLossPercentage;
      map[row.entityId].ytdGainLossPercentage =
        row.ytdGainLossPercentage ?? map[row.entityId].ytdGainLossPercentage;
      map[row.entityId].roiYTD = row.roiYTD ?? map[row.entityId].roiYTD;
    });
    return map;
  }, [data, dataRoiYTD]);
};
export const useDataQuery = () => {
  const definition = useSelector(
    selectDefinitions(SelectionState.COMMITTED)
  )?.[0];
  const [baseParams] = useSelector(selectBaseGroupingParams);
  const glPam = useSelector(selectGlPamValue(SelectionState.COMMITTED));
  const { ids, group_type, dates } = baseParams;
  const periods = [];
  if (definition?.realizedGlMTD || definition?.perRealizedGlMTD) {
    periods.push("MTD");
  }
  if (definition?.realizedGlQTD || definition?.perRealizedGlQTD) {
    periods.push("QTD");
  }
  if (definition?.realizedGlYTD || definition?.perRealizedGlYTD) {
    periods.push("YTD");
  }
  if (
    definition?.perRealizedGlMTD ||
    definition?.perRealizedGlMTD ||
    definition?.perRealizedGlMTD
  ) {
  }
  return useQuery({
    enabled:
      !!definition &&
      (definition.realizedGlMTD ||
        definition.realizedGlQTD ||
        definition.realizedGlYTD ||
        definition.perRealizedGlMTD ||
        definition.perRealizedGlQTD ||
        definition.perRealizedGlYTD ||
        glPam),
    queryKey: [
      scope,
      definition?.realizedGlMTD,
      definition?.realizedGlQTD,
      definition?.realizedGlYTD,
      definition?.perRealizedGlMTD,
      definition?.perRealizedGlQTD,
      definition?.perRealizedGlYTD,
      ids,
      group_type,
      dates,
      glPam,
    ],
    queryFn: async () => {
      const params = {
        endDate: dates,
        identifiers: ids,
        periods,
        showGlPercentage:
          definition?.perRealizedGlMTD ||
          definition?.perRealizedGlQTD ||
          definition?.perRealizedGlYTD,
      };

      if (group_type) {
        params.groupType = group_type === "comp" ? "composite" : group_type;
      } else {
        params.groupType = "entity_list";
      }
      const fetchBuy = async () => {
        const url = new URL(
          `${process.env.REACT_APP_BASE_URL_REALIZED_GL_SERVICE}/rest/realizedPeriodGainLoss`
        );
        url.search = new URLSearchParams({
          ...params,
        }).toString();
        const res = await fetch(url);
        return await res.json();
      };
      if (glPam) {
        params.realizedGlType = "PAM";
      }
      return await fetchBuy();
    },
  });
};

export const useRoiYTDQuery = () => {
  const definition = useSelector(
    selectDefinitions(SelectionState.COMMITTED)
  )?.[0];
  const url = process.env.REACT_APP_BASE_URL_ROIYTD;

  const [baseParams] = useSelector(selectBaseGroupingParams);
  const { ids } = baseParams;
  const selectedModeValue = useSelector(
    selectModeValue(SelectionState.COMMITTED)
  );
  const { asOfDateEntity } = useSelector(selectCommittedEntities);
  const asOfDateString = asOfDateEntity
    ? format(asOfDateEntity, "MM/dd/yyyy")
    : "";
  // /http://ace.loomissayles.com/prd/imt/cismiscservices/roi_ytd_calc?date=04/28/2025&entity_ids=522642,614743
  return useQuery({
    enabled:
      !!definition && definition.roiYTD && selectedModeValue === Mode.ENTITIES,
    queryKey: [scope, definition?.roiYTD, ids, asOfDateEntity],
    queryFn: async () => {
      const { data } = await exchange({
        baseURL: url,
        params: {
          date: asOfDateString,
          entity_ids: ids,
        },
      });
      return await data;
    },
  });
};
